import { useEffect, useState } from "react";
import { authApi, adminApi, messageFrom } from "../api";

const fmtDate = (d) => (d ? String(d).replace("T", " ").slice(0, 16) : "—");
const fmtMoney = (n, c) => `${(Number(n) || 0).toFixed(2)} ${c || ""}`.trim();

function defaultRange() {
    const to = new Date();
    const from = new Date(to.getTime() - 29 * 24 * 3600 * 1000);
    const iso = (d) => d.toISOString().slice(0, 10);
    return { from: iso(from), to: iso(to) };
}

export default function AdminDashboard({ user, onLoggedOut }) {
    const [tab, setTab] = useState("users"); // users | transactions | earnings | token
    const [error, setError] = useState("");

    const [users, setUsers] = useState(null);

    const [range, setRange] = useState(defaultRange());
    const [txFilter, setTxFilter] = useState({ username: "", status: "" });
    const [transactions, setTransactions] = useState(null);

    const [earnings, setEarnings] = useState(null);

    const [tokenInfo, setTokenInfo] = useState(null);
    const [tokenForm, setTokenForm] = useState({ token: "", refresh_token: "" });
    const [savingToken, setSavingToken] = useState(false);

    const logout = () => authApi.logout().finally(onLoggedOut);

    // user management
    const emptyForm = { name: "", username: "", password: "", request_limit: 0, role: "user" };
    const [showCreate, setShowCreate] = useState(false);
    const [createForm, setCreateForm] = useState(emptyForm);
    const [pwUser, setPwUser] = useState(null); // user object for password modal
    const [pwValue, setPwValue] = useState("");
    const [notice, setNotice] = useState("");

    const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(""), 3500); };

    const submitCreate = () => {
        setError("");
        adminApi
            .createUser(createForm)
            .then(() => {
                setShowCreate(false);
                setCreateForm(emptyForm);
                flash("User created");
                loadUsers();
            })
            .catch((err) => setError(messageFrom(err, "Could not create user")));
    };

    const submitPassword = () => {
        setError("");
        adminApi
            .setPassword(pwUser.id, pwValue)
            .then(() => {
                setPwUser(null);
                setPwValue("");
                flash("Password changed");
            })
            .catch((err) => setError(messageFrom(err, "Could not change password")));
    };

    const toggleBlock = (u) => {
        const blocking = u.active === 1;
        if (blocking && !window.confirm(`Block ${u.username}? Their sessions will be terminated.`)) return;
        setError("");
        adminApi
            .setActive(u.id, blocking ? 0 : 1)
            .then(() => { flash(blocking ? "User blocked" : "User unblocked"); loadUsers(); })
            .catch((err) => setError(messageFrom(err, "Could not update user")));
    };

    const removeUser = (u) => {
        if (!window.confirm(`Remove ${u.username} permanently? Their order history stays, but the account is deleted.`)) return;
        setError("");
        adminApi
            .deleteUser(u.id)
            .then(() => { flash("User removed"); loadUsers(); })
            .catch((err) => setError(messageFrom(err, "Could not remove user")));
    };

    const loadUsers = () => {
        setError("");
        adminApi
            .users()
            .then((res) => setUsers(res.data.data))
            .catch((err) => setError(messageFrom(err, "Could not load users")));
    };

    const loadTransactions = () => {
        setError("");
        const params = { from: range.from, to: range.to };
        if (txFilter.username) params.username = txFilter.username;
        if (txFilter.status) params.status = txFilter.status;
        adminApi
            .transactions(params)
            .then((res) => setTransactions(res.data.data))
            .catch((err) => setError(messageFrom(err, "Could not load transactions")));
    };

    const loadEarnings = () => {
        setError("");
        adminApi
            .earnings({ from: range.from, to: range.to })
            .then((res) => setEarnings(res.data.data))
            .catch((err) => setError(messageFrom(err, "Could not load earnings")));
    };

    const loadTokenInfo = () => {
        setError("");
        adminApi
            .getToken()
            .then((res) => setTokenInfo(res.data.data))
            .catch((err) => setError(messageFrom(err, "Could not load token info")));
    };

    const submitToken = () => {
        const payload = {};
        if (tokenForm.token.trim()) payload.token = tokenForm.token.trim();
        if (tokenForm.refresh_token.trim()) payload.refresh_token = tokenForm.refresh_token.trim();
        if (!payload.token && !payload.refresh_token) return;
        setError("");
        setSavingToken(true);
        adminApi
            .setToken(payload)
            .then(() => {
                setTokenForm({ token: "", refresh_token: "" });
                flash("Token updated");
                loadTokenInfo();
            })
            .catch((err) => setError(messageFrom(err, "Could not update token")))
            .finally(() => setSavingToken(false));
    };

    useEffect(() => {
        loadUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (tab === "transactions" && transactions === null) loadTransactions();
        if (tab === "earnings" && earnings === null) loadEarnings();
        if (tab === "token" && tokenInfo === null) loadTokenInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const rangeInputs = (onApply) => (
        <div className="admin__filters">
            <label className="admin__filter">
                <span>From</span>
                <input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} />
            </label>
            <label className="admin__filter">
                <span>To</span>
                <input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} />
            </label>
            <button className="btn btn--primary" onClick={onApply}>Apply</button>
        </div>
    );

    return (
        <div className="dash">
            <nav className="dash__nav">
                <div className="brand">
                    <div className="brand__mark">A</div>
                    <div className="brand__name">Admin Panel</div>
                </div>
                <div className="dash__nav-right">
                    <span className="dash__user">{user?.name || user?.username}</span>
                    <button className="btn btn--ghost" onClick={logout}>Log out</button>
                </div>
            </nav>

            <div className="admin__tabs">
                <button className={`admin__tab ${tab === "users" ? "is-active" : ""}`} onClick={() => setTab("users")}>Users</button>
                <button className={`admin__tab ${tab === "transactions" ? "is-active" : ""}`} onClick={() => setTab("transactions")}>Transactions</button>
                <button className={`admin__tab ${tab === "earnings" ? "is-active" : ""}`} onClick={() => setTab("earnings")}>Earnings</button>
                <button className={`admin__tab ${tab === "token" ? "is-active" : ""}`} onClick={() => setTab("token")}>CARFAX token</button>
            </div>

            {error && <div className="banner banner--error">{error}</div>}
            {notice && <div className="banner banner--ok">{notice}</div>}

            <main className="admin__main">
                {tab === "users" && (
                    <section>
                        <div className="admin__section-head">
                            <h2>Users</h2>
                            <div className="admin__head-actions">
                                <button className="btn btn--ghost" onClick={loadUsers}>Refresh</button>
                                <button className="btn btn--primary" onClick={() => setShowCreate(true)}>+ New user</button>
                            </div>
                        </div>
                        {users === null ? (
                            <div className="admin__empty">Loading…</div>
                        ) : (
                            <div className="admin__table-wrap">
                                <table className="admin__table">
                                    <thead>
                                        <tr>
                                            <th>ID</th><th>Name</th><th>Username</th><th>Role</th><th>Status</th>
                                            <th>Limit</th><th>Used</th><th>Remaining</th>
                                            <th>Reports pulled</th><th>Last report</th><th>API key</th><th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.map((u, index) => {
                                            if(u.username != "maxo-carfax"){
                                                return (
                                                    <tr key={u.id}>
                                                        <td>{index + 1}</td>
                                                        <td>{u.name}</td>
                                                        <td>{u.username}</td>
                                                        <td>{u.role}</td>
                                                        <td>
                                                            <span className={`admin__pill ${u.active ? "admin__pill--ok" : "admin__pill--err"}`}>
                                                                {u.active ? "active" : "disabled"}
                                                            </span>
                                                        </td>
                                                        <td>{u.limit}</td>
                                                        <td>{u.used}</td>
                                                        <td>{u.remaining}</td>
                                                        <td>{u.reports_pulled}</td>
                                                        <td>{fmtDate(u.last_report_at)}</td>
                                                        <td>{u.has_api_key ? u.api_key_masked : "—"}</td>
                                                        <td>
                                                            <div className="admin__row-actions">
                                                                <button className="btn btn--ghost btn--xs" onClick={() => { setPwUser(u); setPwValue(""); }}>Password</button>
                                                                <button className="btn btn--ghost btn--xs" onClick={() => toggleBlock(u)}>
                                                                    {u.active ? "Block" : "Unblock"}
                                                                </button>
                                                                <button className="btn btn--ghost btn--xs btn--danger" onClick={() => removeUser(u)}>Remove</button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )
                                            }
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                )}

                {tab === "transactions" && (
                    <section>
                        <div className="admin__section-head">
                            <h2>Transactions</h2>
                        </div>
                        <div className="admin__filters">
                            <label className="admin__filter">
                                <span>From</span>
                                <input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} />
                            </label>
                            <label className="admin__filter">
                                <span>To</span>
                                <input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} />
                            </label>
                            <label className="admin__filter">
                                <span>Username</span>
                                <input type="text" placeholder="all" value={txFilter.username}
                                    onChange={(e) => setTxFilter({ ...txFilter, username: e.target.value })} />
                            </label>
                            <label className="admin__filter">
                                <span>Status</span>
                                <select value={txFilter.status} onChange={(e) => setTxFilter({ ...txFilter, status: e.target.value })}>
                                    <option value="">all</option>
                                    <option value="created">created</option>
                                    <option value="processing">processing</option>
                                    <option value="completed">completed</option>
                                    <option value="rejected">rejected</option>
                                </select>
                            </label>
                            <button className="btn btn--primary" onClick={loadTransactions}>Apply</button>
                        </div>
                        {transactions === null ? (
                            <div className="admin__empty">Loading…</div>
                        ) : transactions.length === 0 ? (
                            <div className="admin__empty">No transactions in this period</div>
                        ) : (
                            <div className="admin__table-wrap">
                                <table className="admin__table">
                                    <thead>
                                        <tr>
                                            <th>Date</th><th>Username</th><th>Package</th><th>Reports</th>
                                            <th>Amount</th><th>Status</th><th>Credited</th><th>Order ID</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {transactions.map((t) => (
                                            <tr key={t.id}>
                                                <td>{fmtDate(t.created_at)}</td>
                                                <td>{t.username}</td>
                                                <td>{t.package_id}</td>
                                                <td>{t.reports}</td>
                                                <td>{fmtMoney(t.amount, t.currency)}</td>
                                                <td>
                                                    <span className={`admin__pill ${t.status === "completed" ? "admin__pill--ok" : t.status === "rejected" ? "admin__pill--err" : ""}`}>
                                                        {t.status}
                                                    </span>
                                                </td>
                                                <td>{t.credited ? "yes" : "no"}</td>
                                                <td className="admin__mono">{t.external_order_id}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                )}

                {tab === "earnings" && (
                    <section>
                        <div className="admin__section-head">
                            <h2>Earnings</h2>
                        </div>
                        {rangeInputs(loadEarnings)}
                        {earnings === null ? (
                            <div className="admin__empty">Loading…</div>
                        ) : (
                            <>
                                <div className="admin__stats">
                                    {earnings.totals.length === 0 ? (
                                        <div className="admin__empty">No paid orders in this period</div>
                                    ) : (
                                        earnings.totals.map((t) => (
                                            <div className="admin__stat" key={t.currency}>
                                                <div className="admin__stat-value">{fmtMoney(t.total_amount, t.currency)}</div>
                                                <div className="admin__stat-label">
                                                    {t.orders_count} paid orders · {t.reports_sold} reports sold
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                                {earnings.daily.length > 0 && (
                                    <div className="admin__table-wrap">
                                        <table className="admin__table">
                                            <thead>
                                                <tr><th>Day</th><th>Orders</th><th>Amount</th></tr>
                                            </thead>
                                            <tbody>
                                                {earnings.daily.map((d, i) => (
                                                    <tr key={i}>
                                                        <td>{String(d.day).slice(0, 10)}</td>
                                                        <td>{d.orders_count}</td>
                                                        <td>{fmtMoney(d.total_amount, d.currency)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </>
                        )}
                    </section>
                )}

                {tab === "token" && (
                    <section>
                        <div className="admin__section-head">
                            <h2>CARFAX token</h2>
                            <div className="admin__head-actions">
                                <button className="btn btn--ghost" onClick={loadTokenInfo}>Refresh</button>
                            </div>
                        </div>
                        {tokenInfo === null ? (
                            <div className="admin__empty">Loading…</div>
                        ) : (
                            <div className="admin__token">
                                <div className="admin__table-wrap">
                                    <table className="admin__table">
                                        <thead>
                                            <tr><th>Stored value</th><th>Preview</th><th>Length</th></tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>Token</td>
                                                <td className="admin__mono">{tokenInfo.token_preview || "—"}</td>
                                                <td>{tokenInfo.token_length}</td>
                                            </tr>
                                            <tr>
                                                <td>Refresh token</td>
                                                <td className="admin__mono">{tokenInfo.refresh_token_preview || "—"}</td>
                                                <td>{tokenInfo.refresh_token_length}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <p className="admin__hint">
                                    Paste a new value to replace it; a field left empty keeps the current value.
                                    The "Bearer " prefix is added automatically if missing. The new token is used
                                    on the very next report request; the refresh token is picked up by the
                                    auto-refresh cycle.
                                </p>
                                <label className="admin__field">
                                    <span>New token</span>
                                    <textarea rows={4} value={tokenForm.token} spellCheck={false}
                                        placeholder="Bearer eyJhbGciOi…"
                                        onChange={(e) => setTokenForm({ ...tokenForm, token: e.target.value })} />
                                </label>
                                <label className="admin__field">
                                    <span>New refresh token</span>
                                    <textarea rows={3} value={tokenForm.refresh_token} spellCheck={false}
                                        placeholder="v1.M…"
                                        onChange={(e) => setTokenForm({ ...tokenForm, refresh_token: e.target.value })} />
                                </label>
                                <button className="btn btn--primary"
                                    disabled={savingToken || (!tokenForm.token.trim() && !tokenForm.refresh_token.trim())}
                                    onClick={submitToken}>
                                    {savingToken ? "Saving…" : "Save"}
                                </button>
                            </div>
                        )}
                    </section>
                )}
            </main>

            {showCreate && (
                <div className="admin__overlay" onClick={() => setShowCreate(false)}>
                    <div className="admin__modal" onClick={(e) => e.stopPropagation()}>
                        <h3>New user</h3>
                        <label className="admin__field">
                            <span>Name</span>
                            <input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} />
                        </label>
                        <label className="admin__field">
                            <span>Username *</span>
                            <input value={createForm.username} onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })} />
                        </label>
                        <label className="admin__field">
                            <span>Password * (min 6)</span>
                            <input type="password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} />
                        </label>
                        <label className="admin__field">
                            <span>Report limit</span>
                            <input type="number" min="0" value={createForm.request_limit}
                                onChange={(e) => setCreateForm({ ...createForm, request_limit: e.target.value })} />
                        </label>
                        <label className="admin__field">
                            <span>Role</span>
                            <select value={createForm.role} onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}>
                                <option value="user">user</option>
                                <option value="admin">admin</option>
                            </select>
                        </label>
                        <div className="admin__modal-actions">
                            <button className="btn btn--ghost" onClick={() => setShowCreate(false)}>Cancel</button>
                            <button className="btn btn--primary" disabled={!createForm.username || createForm.password.length < 6} onClick={submitCreate}>Create</button>
                        </div>
                    </div>
                </div>
            )}

            {pwUser && (
                <div className="admin__overlay" onClick={() => setPwUser(null)}>
                    <div className="admin__modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Change password — {pwUser.username}</h3>
                        <label className="admin__field">
                            <span>New password (min 6)</span>
                            <input type="password" autoFocus value={pwValue} onChange={(e) => setPwValue(e.target.value)} />
                        </label>
                        <div className="admin__modal-actions">
                            <button className="btn btn--ghost" onClick={() => setPwUser(null)}>Cancel</button>
                            <button className="btn btn--primary" disabled={pwValue.length < 6} onClick={submitPassword}>Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
